import React from 'react';
import { Blockquote, Badge } from '@scale/scaleui-radix';
import {
  CompassScriptLabelType,
  CompassScriptDangerLevel,
} from '@scale/genai-compass-shared/cards/transform_script';

interface Label {
  type: CompassScriptLabelType;
  value: string;
}

export const getLabelDisplayProps = (
  labelType: CompassScriptLabelType,
  labelValue?: string,
): {
  blockquoteColor: 'error' | 'warning' | 'success' | 'neutral' | 'purple' | 'orange' | 'accent';
  blockquoteBg: string;
  badgeColor: 'red' | 'yellow' | 'green' | 'blue' | 'purple' | 'orange' | 'gray';
} => {
  switch (labelType) {
    case CompassScriptLabelType.DANGER_LEVEL:
      if (labelValue === CompassScriptDangerLevel.HIGH) {
        return {
          blockquoteColor: 'error',
          blockquoteBg: 'bg-red-50',
          badgeColor: 'red',
        };
      }
      if (labelValue === CompassScriptDangerLevel.MEDIUM) {
        return {
          blockquoteColor: 'warning',
          blockquoteBg: 'bg-yellow-50',
          badgeColor: 'yellow',
        };
      }
      return {
        blockquoteColor: 'success',
        blockquoteBg: 'bg-green-50',
        badgeColor: 'green',
      };
    case CompassScriptLabelType.CUSTOMER_NAME:
      return {
        blockquoteColor: 'accent',
        blockquoteBg: 'bg-blue-50',
        badgeColor: 'blue',
      };
    case CompassScriptLabelType.ACTION_TYPE:
      return {
        blockquoteColor: 'purple',
        blockquoteBg: 'bg-purple-50',
        badgeColor: 'purple',
      };
    case CompassScriptLabelType.PROJECT_ID:
      return {
        blockquoteColor: 'orange',
        blockquoteBg: 'bg-orange-50',
        badgeColor: 'orange',
      };
    default:
      return {
        blockquoteColor: 'neutral',
        blockquoteBg: 'bg-gray-50',
        badgeColor: 'gray',
      };
  }
};

interface LabelBlockquotesProps {
  projectLabels: Label[];
  customerLabels: Label[];
  actionLabels: Label[];
  dangerLabels: Label[];
}

interface SingleLabelBlockquoteProps {
  labels: Label[];
  labelType: CompassScriptLabelType;
  labelTitle: string;
}

const SingleLabelBlockquote: React.FC<SingleLabelBlockquoteProps> = ({
  labels,
  labelType,
  labelTitle,
}) => {
  // For Danger Level, pass the value of the first label for color/bg
  const blockquoteProps =
    labelType === CompassScriptLabelType.DANGER_LEVEL
      ? getLabelDisplayProps(labelType, labels[0]?.value)
      : getLabelDisplayProps(labelType);
  return (
    <Blockquote size="2" color={blockquoteProps.blockquoteColor} className="p-0">
      <div className={`p-2 rounded-r-sm ${blockquoteProps.blockquoteBg}`}>
        <div className="text-xs text-gray-500 mb-1">{labelTitle}</div>
        <div className="flex gap-2 flex-wrap">
          {labels.map((label, index) => (
            <Badge key={index} color={getLabelDisplayProps(label.type, label.value).badgeColor}>
              {label.value}
            </Badge>
          ))}
        </div>
      </div>
    </Blockquote>
  );
};

const LabelBlockquotes: React.FC<LabelBlockquotesProps> = ({
  projectLabels,
  customerLabels,
  actionLabels,
  dangerLabels,
}) => (
  <div className="flex flex-row gap-4 mt-2">
    {projectLabels.length > 0 && (
      <SingleLabelBlockquote
        labels={projectLabels}
        labelType={CompassScriptLabelType.PROJECT_ID}
        labelTitle="Project ID"
      />
    )}
    {customerLabels.length > 0 && (
      <SingleLabelBlockquote
        labels={customerLabels}
        labelType={CompassScriptLabelType.CUSTOMER_NAME}
        labelTitle="Customer"
      />
    )}
    {actionLabels.length > 0 && (
      <SingleLabelBlockquote
        labels={actionLabels}
        labelType={CompassScriptLabelType.ACTION_TYPE}
        labelTitle="Action Type"
      />
    )}
    {dangerLabels.length > 0 && (
      <SingleLabelBlockquote
        labels={dangerLabels}
        labelType={CompassScriptLabelType.DANGER_LEVEL}
        labelTitle="Danger Level"
      />
    )}
  </div>
);

export default LabelBlockquotes;
